// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 내 현장 목록
//
//  ★ 이 화면에서 제일 중요한 것은 목록이 아니라 **빈 목록**이다.
//    설계상 초대받지 않은 사람은 로그인은 되지만 아무것도 안 보인다(DB-DESIGN §7-5).
//    즉 「빈 화면」이 오류가 아니라 **정상 동작**이고, 그 사람에게는 여기가 막다른 길이다.
//    → 문구는 EmptyState 가 강제한다(그 파일 머리말 참고).
//
//  ★ 폰이 먼저다. 폰에서 카드, 데스크톱에서 표다.
//    ⚠️ 폰이 줄이는 것은 **여백이 아니라 열 개수**다. 행 높이는 오히려 폰이 더 크다
//      (폰 56px · PC 48px). 좁다고 촘촘하게 만들면 장갑으로 못 누른다.
//    ⚠️ **분기는 breakpoint.ts 한 곳에만** 있다. 여기서 useMediaQuery 를 직접 부르지 마라.
//
//  ⚠️ 옛 코드는 slug(`/dangjin`)와 좌표계(`EPSG:5186`)를 **muted 로** 그렸다.
//     그건 라벨이 아니라 **데이터**다 — 소장이 1공구/2공구를 그걸로 구분한다.
//     야외 2.86:1 이라 실제로 안 보인다. muted 는 라벨 전용이다(theme.ts 야외 등급표).
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { Badge, Button, Card, Divider, Group, Stack, Table, Text } from '@mantine/core';
import { getOrgs } from './admin/api';
import { can } from './admin/perms';
import { useSession } from '../auth/SessionProvider';
import { useIsPhone } from '../breakpoint';
import { ColorSchemeControl } from '../components/ColorScheme';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Shell } from '../components/Shell';
import { A } from '../router';
import type { Project } from '../api/client';

/**
 * 목록 한 줄.
 *
 * ⚠️ `/api/projects` 는 `unread`(새 지적 수)를 **이미 준다**(`routes/projects.ts` 의
 *    `UNREAD_SUBQUERY`). 그런데 `api/client.ts` 의 `Project` 에는 아직 그 칸이 없어
 *    여기서 넓혀 쓴다. **`?`(선택)인 것이 중요하다** — 옛 서버(그 줄이 없는 서버)에
 *    붙으면 `undefined` 가 오는데, 그때는 배지를 안 그리는 것이 맞다.
 *    → `client.ts` 에 칸이 생기면 이 별칭은 지우고 `Project` 를 그대로 써라.
 */
type Row = Project & { unread?: number };

/**
 * ★★ **「+ 새 현장」이 켜지는 조건.** [실측 2026-08-07 — 사장님이 못 찾으셔서 알았다]
 *
 *   `can_create_project` 는 **`perms[]` 에 안 들어 있다**(003:102). 현장 권한이 아니라
 *   **조직 권한**이라서다 — 아직 현장이 하나도 없는 사람도 만들 수 있어야 하니 당연하다.
 *   그래서 `/api/projects` 만 봐서는 이 단추를 그릴 수가 없고, `/api/orgs` 를 따로 물어야 한다.
 *
 * ⚠️ **실패해도 조용히 넘어간다.** 이 요청이 죽었다고 현장 목록이 안 뜨면 안 된다 —
 *    목록은 이 화면의 본체이고 이 단추는 곁가지다.
 */
function useCanCreateProject(): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let alive = true;
    getOrgs()
      .then((orgs) => { if (alive) setOk(orgs.some((o) => o.can_create)); })
      .catch(() => { /* 곁가지다. 목록을 막지 않는다 */ });
    return () => { alive = false; };
  }, []);
  return ok;
}

export function Projects() {
  const { state, reload } = useSession();
  const isPhone = useIsPhone();
  const [refreshing, setRefreshing] = useState(false);
  const canCreate = useCanCreateProject();

  // App 의 관문(App.tsx)이 authed 일 때만 이 화면을 그린다. 그래도 타입을 좁혀 둔다 —
  // 나중에 누가 관문을 건너뛰고 이 화면을 직접 라우팅하면 여기서 조용히 터지는 것보다 낫다.
  if (state.status !== 'authed') return null;

  const { projects } = state;

  async function refresh() {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <Shell
      title="내 현장"
      /**
       * ★ 새로고침을 **화면 아래**로 내렸다.
       *   옛 코드는 제목 오른쪽(화면 위)에 있었다. 한 손에 도면이나 무전기를 든 사람은
       *   화면 위쪽에 엄지가 안 닿는다 — global.css 가 스스로 "지금 로그아웃·새로고침이
       *   상단에 있는 것은 잘못이다" 라고 적어 놓고도 코드는 안 고쳐져 있었다.
       *   폰에서는 하단에 고정되고, 데스크톱에서는 그냥 본문 맨 아래에 놓인다(global.css).
       */
      /*
       * ★ 「+ 새 현장」을 **화면 아래**에 둔다. PageHeader 의 `actions` 자리가 아니다 —
       *   그 자리는 「읽기 전용·보조 동작만」이라고 그 파일이 못 박아 뒀고,
       *   현장 만들기는 **주동작**이다. 한 손에 도면 든 사람의 엄지가 닿는 곳에 둔다.
       * ★ 새로고침보다 **앞**에 둔다. 둘 중 사람이 찾아 누르는 것은 이쪽이다.
       */
      bottomBar={
        <>
          {canCreate ? (
            <Button component={A} href="/new" fullWidth style={{ height: 48 }}>
              + 새 현장 만들기
            </Button>
          ) : null}
          <Button
            variant="default"
            onClick={() => void refresh()}
            loading={refreshing}
            fullWidth
            style={{ height: 48 }}
          >
            새로고침
          </Button>
        </>
      }
    >
      <Stack gap="xl">
        <PageHeader
          title="내 현장"
          subtitle={
            projects.length > 0
              ? `${projects.length}개`
              : '지금 이 계정으로 볼 수 있는 현장을 표시합니다'
          }
        />

        {projects.length === 0 ? (
          /**
           * ★★★ 이 문구가 이 화면의 핵심이다 ★★★
           *
           * 비어 있는 이유는 둘 중 하나인데 **화면은 그 둘을 구분할 수 없다.**
           *   ① 정말로 아직 아무 현장에도 초대되지 않았다
           *   ② 현장은 있는데 내가 구성원이 아니다 — 서버가 **일부러** 존재를 숨긴다(404)
           * 그러니 "현장이 없습니다" 는 **거짓말이 될 수 있다.** 실제로는 초대만 받으면
           * 되는 사람에게 "없다" 고 말해 돌려보내는 것이고, 그 사람은 다시 오지 않는다.
           */
          <EmptyState headline="볼 수 있는 현장이 없습니다.">
            로그인은 정상적으로 되었습니다. 다만 아직 어느 현장에도 초대되지 않았거나,
            초대가 <b>다른 이메일 주소로</b> 갔을 수 있습니다. 담당자에게 초대를 요청하세요 —
            이때 <b>지금 로그인한 주소</b>를 같이 알려 주시면 빠릅니다(오른쪽 위 「내 계정」).
          </EmptyState>
        ) : isPhone ? (
          <ProjectCards projects={projects} />
        ) : (
          <ProjectTable projects={projects} />
        )}

        {/* ★★ 화면 밝기 토글. **전 저장소에서 어디에도 안 걸려 있었다**(grep 0건). ★★
            즉 터널·야간용으로 만들어 둔 어두운 화면을 **켤 방법이 화면에 없었고**, 더 나쁘게는
            localStorage 에 이미 `dark` 가 들어 있는 기기(예전에 auto 로 잡혔던 폰)는
            `seedLightDefault()` 가 일부러 안 건드리므로 **거기서 나올 방법도 없었다.**

            ⚠️ ColorScheme.tsx 는 「설정 화면에 둔다. 머리글에 두지 마라」고 적어 뒀다.
               설정 화면이 아직 없어서 **내 현장**(모든 사람이 지나가고, 어느 화면에서든
               로고로 돌아오는 곳) 맨 아래에 뒀다. 머리글이 아니라 본문 끝이라
               「급할 때 잘못 눌린다」는 위험은 없다.
            → 설정 화면이 생기면 **이 두 줄을 그리로 옮겨라.** */}
        <Divider />
        <ColorSchemeControl />
      </Stack>
    </Shell>
  );
}

// ═══ 정지된 현장 ══════════════════════════════════════════════════════════

/**
 * `state === 'suspended'` 인 현장에 붙는 배지.
 *
 * ⚠️⚠️ [정정 2026-08-13] 여기 *「목록에는 뜨되 들어갈 수 없다」*고 적혀 있었다.
 *    **거짓이고, 그래서 이 함수는 지금 한 번도 안 돈다.** RLS 정책 `project_select`
 *    (`db/004_rls.sql:93`)가 `app.my_projects('project.view')` 를 쓰고 그 함수가
 *    `pr.state = 'active'` 로 거르므로(`db/003_functions.sql:94`) **정지된 현장은
 *    목록에 줄 자체가 안 온다.** 같은 거짓이 `api/client.ts` 의 `Project.state` 에도
 *    한 벌 있었다 — 같이 고쳤다.
 *    ⓘ 그래도 **지우지 않는다.** 아래 두 ★ 는 「정지 현장을 목록에 보여주기로 정하는 날」
 *      그대로 유효한 판단이고, 그때 고칠 곳은 이 화면이 아니라 위 두 SQL 이다.
 *
 * ★ 이걸 **안 그리면** 사용자는 카드를 눌렀는데 아무 일도 안 일어나는 것을 겪고,
 *   그게 앱 고장으로 읽힌다. 「왜 안 되는지」는 누르기 **전에** 있어야 한다.
 * ★ 색이 아니라 **글자**로 말한다. 야외에서 색은 뭉개진다(theme.ts 제1원칙).
 */
function StateBadge({ project }: { project: Project }) {
  if (project.state !== 'suspended') return null;
  return (
    <Badge
      style={{ borderColor: 'var(--cv-warn)', color: 'var(--cv-warn)', background: 'var(--cv-warn-bg)' }}
    >
      정지됨 · 들어갈 수 없습니다
    </Badge>
  );
}

// ═══ 새 지적 ══════════════════════════════════════════════════════════════
//
//  ★★★ **뷰어를 안 열고도 「남이 올린 지적이 있다」를 아는 유일한 자리다.** ★★★
//
//  감리는 현장에서 지적을 올리고 시공은 사무실에서 답한다. 그런데 여태 **남이 올린
//  지적이 있다는 사실을 화면 어디에서도 안 알렸다** — 뷰어를 열고 이슈 창까지
//  들어가야만 알 수 있었다. 조작이 서툰 발주처가 제일 먼저(그리고 자주 여기까지만)
//  보는 화면이 이 목록이라, **말할 자리는 여기다.**
//
//  ★ 셈은 서버가 한다(`app.issue_unread` 뷰 · 017 ⑥). 이슈 창의 배지와 **같은 뷰**라
//    「목록엔 3인데 들어가면 2」가 되지 않는다. 화면은 숫자를 다시 만들지 않는다.
//  ★ **내가 올린 것은 안 센다.** 올린 순간 서버가 읽음을 같이 찍는다
//    (`routes/issues.ts` 의 postIssue). 안 그러면 감리가 지적을 올릴 때마다 자기
//    화면에 숫자가 붙고, 사람은 그 숫자를 곧 무시하게 된다.
//
//  ⚠️ 뷰어의 이슈 창은 같은 값을 **「안 읽음 3」**이라고 쓴다(IssuePanel 의 거르개 칩).
//     여기서 낱말을 바꾼 것은 **문맥이 없어서**다 — 이슈 창 안에서는 「안 읽음」이 뭘
//     말하는지 자명하지만, 현장 목록에서 「안 읽음 3」은 무엇이 3인지 알 수 없다.
//     낱말을 맞추고 싶으면 **이 화면이 아니라 저쪽을** 「새 지적」으로 바꿔라.

/** 배지에 쓸 문구. 그릴 것이 없으면 `null`. */
export function unreadLabel(unread?: number): string | null {
  // ⚠️ `unread ?? 0` 으로 줄이지 마라. 그러면 「칸이 없다(모름)」와 「0건」이 한 덩어리가
  //    되는데, 둘은 다른 사실이다 — 앞은 아무 말도 하면 안 되고 뒤는 안 그리는 것이 맞다.
  //    지금은 결과가 같아 보이지만, 나중에 「0건」을 회색으로라도 보이자는 말이 나오면
  //    그 구분이 없어서 **거짓말하는 화면**이 된다. `Projects.spec.ts` 가 둘을 따로 잰다.
  if (typeof unread !== 'number' || !Number.isFinite(unread) || unread <= 0) return null;
  return `새 지적 ${unread}건`;
}

/**
 * ⚠️ **12px(Badge 기본)이 아니라 14px 이다.** 기본 크기는 「정지됨」 같은 **라벨**에
 *   맞춰 둔 값이고, 이건 사람이 보고 움직이는 **숫자**다 — theme.ts 가 "muted 는
 *   라벨 전용, 정보에 쓰지 마라" 라고 적은 것과 같은 이야기다.
 *   (아직 폰 실물로 못 재 봤다. 야외에서 안 읽히면 **여기만 키우지 말고** theme.ts 의
 *    Badge 기본값을 올려라 — 한 배지만 커지면 그 다음 사람이 이유를 모른다.)
 * ⚠️ 색은 강조색(`--cv-accent` · 흰 위 6.36:1)이고 「정지됨」의 주의색과 **일부러
 *   다르다** — 하나는 막힌 것이고 하나는 할 일이다.
 * ⚠️ 그래도 **색에 기대지 않는다.** 「새 지적」이라고 글자로 쓴다(theme.ts 제1원칙:
 *   야외에서 색은 뭉개진다).
 */
function UnreadBadge({ unread }: { unread?: number }) {
  const label = unreadLabel(unread);
  if (!label) return null;
  return (
    <Badge
      style={{
        borderColor: 'var(--cv-accent)',
        color: 'var(--cv-accent)',
        background: 'var(--cv-accent-bg)',
        fontSize: '0.875rem',
      }}
    >
      {label}
    </Badge>
  );
}

// ═══ 현장별 관리 바로가기 ══════════════════════════════════════════════════

/**
 * ★★★ **이것이 관리 화면으로 들어가는 유일한 문이다.** ★★★
 *
 * [실측 2026-08-07] 사장님이 *"admin계정은 project 생성할수 있어야 하는데 그거 창이
 * 어디 있는지 모르겠어"* 라고 하셔서 전수로 훑었더니, 화면 7개 중 **6개가 어디서도
 * 갈 수 없는 상태**였다:
 *
 *     /new              현장 만들기   ← 링크 0건
 *     /{slug}/modules   기능 설정     ← 링크 0건
 *     /{slug}/trash     휴지통        ← 링크 0건
 *     /{slug}/share     공유 링크     ← 링크 0건
 *     /{slug}/members   구성원        ← 공유·기능 화면에서만 (그 둘도 못 들어감)
 *     /{slug}/upload    올리기        ← 휴지통에서만 (휴지통도 못 들어감)
 *
 * 관리 화면들끼리는 서로 링크가 있어서 **자기들끼리는 멀쩡해 보였다.** 바깥에서 그 섬으로
 * 들어가는 배가 없다는 것을 아무도 못 봤다 — 각 화면을 만든 사람은 자기 화면을
 * 주소로 직접 열어 확인했기 때문이다. **화면을 만들 때는 「거기로 어떻게 가는가」를
 * 같이 만들어야 한다.**
 *
 * ★ 게이트는 `can()` 하나로 한다. 그 함수는 **서버가 준 `perms[]` 와 「화면이 실제로
 *   있는 키」의 교집합**만 참으로 만든다(`admin/perms.ts` 의 IMPLEMENTED).
 *   `perms.includes()` 를 직접 쓰면 **라우트가 없는 버튼**이 생기고, 누르면 404 다.
 *
 * ⚠️ 여기는 **보안이 아니다.** 진짜 판정은 서버가 한다(비구성원·권한없음 전부 404).
 *   그래서 버튼이 잘못 떠도 새는 것은 없고, 반대로 **안 뜨면 기능이 통째로 사라진다** —
 *   실제로 지금까지 그랬다. 애매하면 **그리는 쪽**으로 기울여라.
 *
 * ⚠️ 「정지됨」 현장에는 안 그린다. 들어가도 서버가 막는다.
 */
const ADMIN_LINKS: readonly { sub: string; label: string; perm: string }[] = [
  { sub: 'upload', label: '올리기', perm: 'upload.view' },
  { sub: 'members', label: '구성원', perm: 'member.view' },
  { sub: 'modules', label: '기능', perm: 'module.view' },
  { sub: 'trash', label: '휴지통', perm: 'trash.view' },
  { sub: 'share', label: '공유', perm: 'share.view' },
  // ★ [025] 선형. **읽기 권한(`model.view`)으로 건다** — 열람자도 뷰어에서 선형을
  //   골라야 하므로 「어떤 선형이 있는지」를 볼 수 있어야 한다. 올리기는 화면 안에서
  //   서버의 `can.manage` 가 판정한다(여기서 역할로 가르면 org admin 이 조용히 빠진다).
  { sub: 'alignments', label: '선형', perm: 'model.view' },
  /*
   * ⚠️⚠️ **「도면」 단추를 없앴다** [2026-08-17 · db/029 · 사장님 지적]
   *   *"3dtile이랑 ifc 올리는거처럼 올리게 해달라니깐 이해를 못한거 같네"*
   *     올리기 → 「올리기」 (IFC·ZIP 과 같은 화면)
   *     고르기 → 뷰어의 레이어 패널 「도면(DXF)」 줄
   *     지우기 → 「올리기」 화면의 「올린 자료 고치기 · 지우기」
 *              (도면은 `app.model` 의 딸림 표라 모델을 지우면 같이 간다)
 *     되살리기 → 「휴지통」
 *   ⚠️ [고침 2026-09-04] 「지우기 → 휴지통」은 거짓이었다 — 그 화면에는 지우는
 *      단추가 없다(`Trash.tsx` 가 *"파괴적 동작을 이 화면에 두지 않는다"* 고 적어 뒀다).
   *   ★ 되살리지 마라. 단추가 있으면 사람은 **거기서 올리려 한다** — 그것이 사장님이
   *     겪으신 것이다.
   *   ⓘ 선형(`/alignments`)은 그대로 둔다. 사장님이 도면만 지적하셨고, 선형은 지금
   *     별도 화면이 문제를 일으키지 않는다 — 근거 없이 같이 옮기지 않는다.
   */
];

function AdminLinks({ project }: { project: Project }) {
  if (project.state === 'suspended') return null;
  const items = ADMIN_LINKS.filter((l) => can(project.perms, l.perm));
  if (items.length === 0) return null; // 열람자에게는 한 줄도 안 생긴다

  return (
    <Group gap={6} wrap="wrap">
      {items.map((l) => (
        <Button
          key={l.sub}
          component={A}
          href={`/${project.slug}/${l.sub}`}
          variant="default"
          size="compact-sm"
          /* 44px — 야외에서 장갑 낀 손. global.css 의 터치 규칙과 같은 값이다. */
          style={{ minHeight: 44, paddingInline: 12 }}
        >
          {l.label}
        </Button>
      ))}
    </Group>
  );
}

// ═══ 폰 — 카드 ════════════════════════════════════════════════════════════

function ProjectCards({ projects }: { projects: Row[] }) {
  return (
    <Stack gap="md" component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {projects.map((p) => (
        <li key={p.id}>
          {/*
            ⚠️ `.cv-tap` 은 `display:block` 이어야 한다.
               옛 global.css 의 `a:not(.mantine-Button-root)` 규칙이 특정도 (0,2,1) 로
               `.cv-tap`(0,1,0)을 이기면서 이 카드에 `inline-flex; align-items:center` 를
               걸어 **카드 안 내용이 전부 가운데로 뭉쳤다.** 폭은 Stack 안에서 살아나므로
               아무도 「버그」로 신고하지 않고 "좀 이상하다" 로 끝났다.
               지금은 링크 터치 규칙이 opt-in(.cv-tap-link) 이라 이 카드를 안 건드린다.
          */}
          <Card component={A} href={`/${p.slug}`} className="cv-tap" padding="md">
            <Stack gap="xs">
              {/* 현장 이름 — 16px 굵게 ink. 야외 A 등급.
                  ★ 「새 지적」은 **이름 옆**이다. 아래 belt·모델 줄에 끼우면 보조 정보로
                    읽히는데, 이건 사람을 움직이게 하려고 있는 값이다.
                  ⚠️ `wrap="wrap"` 을 지우지 마라. 현장 이름은 「당진~천안 고속도로 2공구」
                     처럼 길어질 수 있고, 폰에서 한 줄로 묶으면 이름이나 배지 중 하나가
                     잘린다. 접히는 편이 낫다 — 여기서 잘려도 되는 글자는 없다. */}
              <Group gap="sm" wrap="wrap">
                <Text size="md" fw={700}>
                  {p.name}
                </Text>
                <UnreadBadge unread={p.unread} />
              </Group>

              {/* ★ slug 는 **데이터**다. 16px · 고정폭 · ink-2.
                  소장이 1공구/2공구를 이걸로 구분한다. muted(2.86:1)로 그리면 안 보인다. */}
              <Text size="md" className="cv-mono" c="var(--cv-ink-2)">
                /{p.slug}
              </Text>

              <Group gap="sm" wrap="wrap">
                {/* 좌표 원점대. 라틴 전용이라 mono+tracking 이 맞는 자리다(.cv-unit). */}
                <span className="cv-unit">{p.belt}</span>
                {typeof p.model_count === 'number' ? (
                  <Text size="sm" c="var(--cv-ink-2)" className="cv-num">
                    모델 {p.model_count}건
                  </Text>
                ) : null}
                <StateBadge project={p} />
              </Group>
            </Stack>
          </Card>

          {/* ⚠️ **카드 `<a>` 밖이다.** 안에 넣으면 링크 안의 링크가 되는데, 그건
              잘못된 HTML 이고 실제로도 두 링크가 클릭을 다툰다(카드로 들어가 버린다).
              그래서 카드 아래에 따로 둔다 — 카드 전체가 「뷰어로」 이고, 이 줄이 「관리」다. */}
          <div style={{ marginTop: 8 }}>
            <AdminLinks project={p} />
          </div>
        </li>
      ))}
    </Stack>
  );
}

// ═══ 데스크톱 — 표 ════════════════════════════════════════════════════════

function ProjectTable({ projects }: { projects: Row[] }) {
  return (
    // 표가 좁은 화면에서 몸통을 밀어내지 않게 감싼다.
    <Table.ScrollContainer minWidth={520}>
      <Table withTableBorder>
        <Table.Thead>
          <Table.Tr>
            {/* ★ scope="col" — 없으면 스크린리더가 셀을 읽을 때 **어느 열인지 말해 주지
                않는다.** 옛 코드에 빠져 있었다. 표는 열 제목이 있어야 표다. */}
            <Table.Th scope="col">현장</Table.Th>
            <Table.Th scope="col">주소</Table.Th>
            <Table.Th scope="col">좌표 원점대</Table.Th>
            <Table.Th scope="col">모델</Table.Th>
            {/* 관리 열. 열람자에게는 칸이 늘 비지만 **열 자체는 남긴다** —
                사람마다 열 개수가 다르면 같은 화면을 보며 이야기할 때 서로 헷갈린다. */}
            <Table.Th scope="col">관리</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {projects.map((p) => (
            <Table.Tr key={p.id}>
              <Table.Td>
                {/* 행 전체가 아니라 **이름이 링크**다. 행에 onClick 을 걸면 텍스트를
                    드래그해 복사할 수 없고, 키보드로도 못 간다. */}
                <Group gap="sm" wrap="wrap">
                  <A
                    href={`/${p.slug}`}
                    className="cv-tap-link"
                    style={{ fontWeight: 700, color: 'inherit' }}
                  >
                    {p.name}
                  </A>
                  {/* ★ **열을 새로 만들지 않는다.** 열이 하나 늘면 사람마다·현장마다
                      화면 폭이 달라지고, 무엇보다 새 지적이 없는 날에는 그 열이 통째로
                      빈칸이다. 이름 옆이면 배지가 있을 때만 자리를 차지한다. */}
                  <UnreadBadge unread={p.unread} />
                  <StateBadge project={p} />
                </Group>
              </Table.Td>
              {/* muted 아님. 표 안이라고 데이터가 라벨이 되지는 않는다. */}
              <Table.Td className="cv-mono">/{p.slug}</Table.Td>
              <Table.Td className="cv-unit">{p.belt}</Table.Td>
              <Table.Td className="cv-num">
                {typeof p.model_count === 'number' ? `${p.model_count}건` : '—'}
              </Table.Td>
              <Table.Td>
                <AdminLinks project={p} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
